import {
  withPublicInventoryDemo,
  demoInventoryConnection,
  saveDemoInventoryConnection,
  syncDemoInventory,
  demoInventorySnapshot,
  demoInventorySyncRuns,
  demoInventoryStores,
  updateDemoInventoryStore,
} from '../dev/inventory-demo-data'
import { withDemoUi } from '../dev/demoMode'
import { apiRequest } from './client'

export type InventoryProvider = 'FAKE' | 'IIKO' | 'RKEEPER'
export type InventoryStoreRole = 'KITCHEN' | 'BAR' | 'IGNORE'

export type InventoryConnectionDto = {
  provider: InventoryProvider
  enabled: boolean
  demo: boolean
}

export type InventoryStoreDto = {
  id: number
  external_id: string
  name: string
  role: InventoryStoreRole
}

export type InventorySnapshotDto = {
  provider: InventoryProvider
  demo: boolean
  accounting_date: string
  synced_at: string | null
  error: string | null
  groups: unknown[]
}

export type InventorySyncRunDto = {
  id: number
  accounting_date: string
  started_at: string
  finished_at: string | null
  status: 'RUNNING' | 'OK' | 'FAILED'
  error: string | null
}

export const inventoryPath = (organizationId: number, venueId: number) =>
  `/organizations/${organizationId}/venues/${venueId}/inventory`

const demoConnection: InventoryConnectionDto = { provider: 'FAKE', enabled: true, demo: true }
const demoStores: InventoryStoreDto[] = [
  { id: 1, external_id: 'demo-kitchen', name: 'Кухня', role: 'KITCHEN' },
  { id: 2, external_id: 'demo-bar', name: 'Бар', role: 'BAR' },
]

const loadInventoryConnectionFromApi = (organizationId: number, venueId: number) =>
  withDemoUi(demoConnection, () =>
    apiRequest<InventoryConnectionDto>(`${inventoryPath(organizationId, venueId)}/connection`),
  )

const saveInventoryConnectionFromApi = (
  organizationId: number,
  venueId: number,
  provider: 'FAKE' | 'IIKO',
  secret: Record<string, string> | null,
) =>
  withDemoUi({ provider, enabled: true, demo: provider === 'FAKE' }, () =>
    apiRequest<InventoryConnectionDto>(`${inventoryPath(organizationId, venueId)}/connection`, {
      method: 'POST',
      body: { provider, secret },
    }),
  )

const syncInventoryFromApi = (organizationId: number, venueId: number) =>
  withDemoUi(
    {
      provider: 'FAKE' as const,
      demo: true,
      accounting_date: new Date().toISOString().slice(0, 10),
      synced_at: new Date().toISOString(),
      error: null,
      groups: [],
    },
    () =>
      apiRequest<InventorySnapshotDto>(`${inventoryPath(organizationId, venueId)}/sync`, {
        method: 'POST',
      }),
  )

const loadInventorySnapshotFromApi = (organizationId: number, venueId: number) =>
  withDemoUi(
    {
      provider: 'FAKE' as const,
      demo: true,
      accounting_date: new Date().toISOString().slice(0, 10),
      synced_at: null,
      error: null,
      groups: [],
    },
    () => apiRequest<InventorySnapshotDto>(`${inventoryPath(organizationId, venueId)}/snapshot`),
  )

const loadInventorySyncRunsFromApi = (organizationId: number, venueId: number) =>
  withDemoUi<InventorySyncRunDto[]>([], () =>
    apiRequest<InventorySyncRunDto[]>(`${inventoryPath(organizationId, venueId)}/sync-runs`),
  )

const loadInventoryStoresFromApi = (organizationId: number, venueId: number) =>
  withDemoUi(demoStores, () =>
    apiRequest<InventoryStoreDto[]>(`${inventoryPath(organizationId, venueId)}/stores`),
  )

const updateInventoryStoreRoleFromApi = (
  organizationId: number,
  venueId: number,
  storeId: number,
  role: InventoryStoreRole,
) =>
  withDemoUi(
    {
      ...(demoStores.find((store) => store.id === storeId) ?? {
        id: storeId,
        external_id: `demo-${storeId}`,
        name: 'Склад',
        role,
      }),
      role,
    },
    () =>
      apiRequest<InventoryStoreDto>(`${inventoryPath(organizationId, venueId)}/stores/${storeId}`, {
        method: 'PATCH',
        body: { role },
      }),
  )

export function loadInventoryConnection(organizationId: number, venueId: number) {
  return withPublicInventoryDemo(
    () => demoInventoryConnection({ organizationId, venueId }),
    () => loadInventoryConnectionFromApi(organizationId, venueId),
  )
}
export function saveInventoryConnection(
  organizationId: number,
  venueId: number,
  provider: 'FAKE' | 'IIKO',
  secret: Record<string, string> | null,
) {
  return withPublicInventoryDemo(
    () => saveDemoInventoryConnection({ organizationId, venueId }, provider),
    () => saveInventoryConnectionFromApi(organizationId, venueId, provider, secret),
  )
}
export function syncInventory(organizationId: number, venueId: number) {
  return withPublicInventoryDemo(
    () => syncDemoInventory({ organizationId, venueId }),
    () => syncInventoryFromApi(organizationId, venueId),
  )
}
export function loadInventorySnapshot(organizationId: number, venueId: number) {
  return withPublicInventoryDemo(
    () => demoInventorySnapshot({ organizationId, venueId }),
    () => loadInventorySnapshotFromApi(organizationId, venueId),
  )
}
export function loadInventorySyncRuns(organizationId: number, venueId: number) {
  return withPublicInventoryDemo(
    () => demoInventorySyncRuns({ organizationId, venueId }),
    () => loadInventorySyncRunsFromApi(organizationId, venueId),
  )
}
export function loadInventoryStores(organizationId: number, venueId: number) {
  return withPublicInventoryDemo(
    () => demoInventoryStores({ organizationId, venueId }),
    () => loadInventoryStoresFromApi(organizationId, venueId),
  )
}
export function updateInventoryStoreRole(
  organizationId: number,
  venueId: number,
  storeId: number,
  role: InventoryStoreRole,
) {
  return withPublicInventoryDemo(
    () => updateDemoInventoryStore({ organizationId, venueId }, storeId, role),
    () => updateInventoryStoreRoleFromApi(organizationId, venueId, storeId, role),
  )
}
